import { Link } from 'react-router-dom';
import { formatUsage } from '../utils/dex';
import { toQuery } from '../utils/pokemon';

function ShareList({ title, items, empty }) {
  return (
    <div>
      <h3>{title}</h3>
      {items?.length ? (
        <ul>
          {items.map((item) => (
            <li key={`${title}-${item.id || item.name || item.label || item.nature}-${item.evText || ''}`}>
              <span>{item.nature || item.name}</span>
              {item.evText ? <span> {item.evText}</span> : null}
              <span> {formatUsage(item.percent)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{empty}</p>
      )}
    </div>
  );
}

function SourcePill({ children }) {
  return <span className="source-pill">{children}</span>;
}

export function usageComparePath(targetName, agentName) {
  const left = toQuery(targetName);
  const right = toQuery(agentName);
  if (!left || !right) return '/compare';
  return `/compare?with=${encodeURIComponent(left)}&against=${encodeURIComponent(right)}`;
}

function UsageSets({ usage, loading, error, onRetry }) {
  if (loading) return <p className="status" role="status">Loading Smogon sets…</p>;
  if (error) {
    return (
      <div className="alert" role="alert">
        <p>{error}</p>
        {onRetry && <button className="btn btn-primary" type="button" onClick={onRetry}>Retry</button>}
      </div>
    );
  }
  if (!usage) return null;
  if (!usage.available) {
    return (
      <section className="usage-panel" aria-label="Smogon sets">
        <h2>How it is used</h2>
        <p>{usage.message || 'Smogon did not publish a moveset for this Pokémon.'}</p>
      </section>
    );
  }

  const profile = usage.profile;
  const ivNotes = [];
  (profile.spreads || []).forEach((spread) => {
    (spread.ivNotes || []).forEach((note) => {
      const existing = ivNotes.find((item) => item.kind === note.kind && item.text === note.text);
      if (existing) existing.natures.push(spread.nature);
      else ivNotes.push({ ...note, natures: [spread.nature] });
    });
  });

  return (
    <section className="usage-panel" aria-label="Smogon sets">
      <div className="tier-heading">
        <h2>How it is used</h2>
        <p>
          <SourcePill>Smogon</SourcePill>
          {' '}
          {usage.tier || 'Tier'} · {usage.monthLabel}
          {typeof profile.usage === 'number' ? ` · ${formatUsage(profile.usage)} ladder usage` : ''}
        </p>
      </div>
      <p className="source-line">
        Set shares for {usage.monthLabel}, from{' '}
        <a href={usage.chaosUrl} rel="noreferrer">Smogon chaos data</a>.
        {' '}A move at 25% means a quarter of this Pokémon’s recorded move weight, not a quarter of all teams.
        {usage.warning ? ` ${usage.warning}` : ''}
      </p>
      <div className="set-grid">
        <ShareList title="Moves" items={profile.moves} empty="No moves in this file." />
        <ShareList title="Items" items={profile.items} empty="No items in this file." />
        <ShareList title="Abilities" items={profile.abilities} empty="No abilities in this file." />
        <ShareList title="Tera types" items={profile.teraTypes} empty="No Tera types in this file." />
        <ShareList title="Teammates" items={profile.teammates} empty="No teammates in this file." />
        <ShareList title="Natures and EVs" items={profile.spreads} empty="No spreads in this file." />
      </div>
      {ivNotes.length > 0 && (
        <ul className="convention-list">
          {ivNotes.map((note) => (
            <li key={`${note.kind}-${note.text}`}>
              <SourcePill>Convention</SourcePill>
              {' '}
              {note.natures.join(', ')}: {note.text}
            </li>
          ))}
        </ul>
      )}
      {usage.agents?.available && (
        <p className="toolbar">
          <Link className="btn btn-secondary" to={`/pokemon/${usage.pokemon.slug || usage.pokemon.id}/agents`}>
            Agents
          </Link>
        </p>
      )}
    </section>
  );
}

export default UsageSets;
